import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useTheme, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import Sidebar from '@/components/Sidebar';
import ThemeLanguageToggle from '@/components/ThemeLanguageToggle';
import {
  Menu,
  Facebook,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  AlertCircle,
  Unlink,
  ExternalLink
} from 'lucide-react';

const MessengerOnboarding = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedChatbot, setSelectedChatbot] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  
  // Check URL params for OAuth callback
  const step = searchParams.get('step');
  const error = searchParams.get('error');
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (error) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to get access from Facebook. Please try again.',
        'no_pages_found': 'No Facebook Pages found. Make sure you are an admin of a Page.',
        'failed_to_get_pages': 'Failed to retrieve Page list. Please try again.',
        'invalid_state': 'Login session is invalid. Please try again.',
        'missing_params': 'Missing parameters. Please try again.',
        'callback_failed': 'An error occurred. Please try again.'
      };
      toast.error(errorMessages[error] || `Error: ${error}`);
    }
    
    fetchData();
  }, []);

  useEffect(() => {
    if (step === 'select-page') {
      fetchPages();
    }
  }, [step]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, chatbotsRes] = await Promise.all([
        axios.get(`${API}/messenger/status`, { headers }),
        axios.get(`${API}/chatbots`, { headers })
      ]);
      setStatus(statusRes.data);
      setChatbots(chatbotsRes.data || []);
      
      // If already connected with agent assigned, redirect to channels
      if (statusRes.data?.connected && statusRes.data?.chatbot_id) {
        navigate('/channels', { replace: true });
        return;
      }
      
      // Set selected agent from current status
      if (statusRes.data?.chatbot_id) {
        setSelectedAgent(statusRes.data.chatbot_id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/messenger/oauth/pages`, { headers });
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent) {
      toast.error('Please select an AI Agent first');
      return;
    }
    
    try {
      setUpdatingAgent(true);
      const response = await axios.put(`${API}/messenger/update-agent`, {
        chatbot_id: selectedAgent
      }, { headers });
      
      toast.success(response.data.message);
      
      // Redirect to channels page after saving agent
      navigate('/channels', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change AI Agent');
    } finally {
      setUpdatingAgent(false);
    }
  };

  const handleConnectFacebook = () => {
    // Redirect to Facebook OAuth with user_id
    if (!user?.id) {
      toast.error('Please log in first');
      navigate('/login?redirect=messenger-setup');
      return;
    }
    // Use window.location.origin for dynamic redirect URL across all environments
    window.location.href = `${window.location.origin}/api/messenger/oauth/login?user_id=${user.id}`;
  };

  const handleSelectPage = async () => {
    if (!selectedPage || !selectedChatbot) {
      toast.error('Please select a Page and an AI Agent first');
      return;
    }
    
    try {
      setConnecting(true);
      const response = await axios.post(`${API}/messenger/oauth/select-page`, {
        page_id: selectedPage,
        chatbot_id: selectedChatbot
      }, { headers });
      
      toast.success(response.data.message);
      
      // Redirect to channels page after successful connection
      navigate('/channels', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Page');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Messenger?')) return;
    
    try {
      setDisconnecting(true);
      await axios.delete(`${API}/messenger/disconnect`, { headers });
      toast.success('Messenger disconnected successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect Messenger');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
        <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] transition-colors">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="lg:ml-64">
          <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 dark:text-zinc-300">
                  <Menu className="w-6 h-6" />
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="text-slate-600 dark:text-zinc-300 hover:text-slate-900 dark:hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
                      Facebook Messenger
                    </h1>
                    <p className="text-sm text-slate-600 dark:text-zinc-400">
                      Connect your Facebook Page
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <ThemeLanguageToggle />
              </div>
            </div>
          </header>

          <main className="p-4 lg:p-8 max-w-2xl mx-auto">
            
            {/* Already Connected */}
            {status?.connected && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">
                        {status.page_name}
                      </h2>
                      <p className="text-sm text-slate-600 dark:text-zinc-400">
                        Connected • Page ID: {status.page_id}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="gap-2"
                  >
                    {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                    Disconnect
                  </Button>
                </div>
                
                {/* AI Agent Selection */}
                <div className="border-t border-slate-200 dark:border-zinc-800 pt-6">
                  <Label className="text-slate-700 dark:text-zinc-300 mb-2 block font-medium">
                    Assigned AI Agent
                  </Label>
                  <div className="flex gap-3">
                    <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                      <SelectTrigger className="flex-1 bg-slate-50 dark:bg-zinc-800 dark:text-zinc-200">
                        <SelectValue placeholder="Select AI Agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {chatbots.map((bot) => (
                          <SelectItem key={bot.id} value={bot.id}>
                            {bot.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleUpdateAgent}
                      disabled={updatingAgent || !selectedAgent || selectedAgent === status.chatbot_id}
                      className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                    >
                      {updatingAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Save
                    </Button>
                  </div>
                  
                  {/* Current agent info */}
                  {status.chatbot_name ? (
                    <p className="text-sm text-slate-500 dark:text-zinc-400 mt-2">
                      Current: <span className="font-medium text-slate-700 dark:text-zinc-300">{status.chatbot_name}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                      No AI Agent selected yet. Select one to enable auto-reply.
                    </p>
                  )}
                  
                  {chatbots.length === 0 && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                      You don't have any AI Agents yet. <a href="/dashboard" className="underline">Create one</a> first.
                    </p>
                  )}
                </div>
                
                <div className="mt-6 p-4 bg-slate-50 dark:bg-zinc-800/50 rounded-xl">
                  <p className="text-sm text-slate-600 dark:text-zinc-300">
                    Incoming messages to your Facebook Page will be automatically replied by Nerava AI.
                  </p>
                </div>
              </div>
            )}

            {/* Page Selection (after OAuth) */}
            {step === 'select-page' && pages.length > 0 && !status?.connected && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6 mb-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Facebook className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-zinc-50 mb-2">
                    Select a Facebook Page
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-zinc-400">
                    Choose which Page you want to connect with Nerava
                  </p>
                </div>
                
                  <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700 dark:text-zinc-300 mb-2 block">Facebook Page</Label>
                    <Select value={selectedPage} onValueChange={setSelectedPage}>
                      <SelectTrigger className="bg-slate-50 dark:bg-zinc-800 dark:text-zinc-200">
                        <SelectValue placeholder="Select a Page" />
                      </SelectTrigger>
                      <SelectContent>
                        {pages.map((page) => (
                          <SelectItem key={page.id} value={page.id}>
                            <div className="flex items-center gap-2">
                              {page.picture && (
                                <img src={page.picture} alt="" className="w-5 h-5 rounded-full" />
                              )}
                              {page.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700 dark:text-zinc-300 mb-2 block">Select AI Agent</Label>
                    <Select value={selectedChatbot} onValueChange={setSelectedChatbot}>
                      <SelectTrigger className="bg-slate-50 dark:bg-zinc-800 dark:text-zinc-200">
                        <SelectValue placeholder="Select an AI Agent to handle messages" />
                      </SelectTrigger>
                      <SelectContent>
                        {chatbots.map((bot) => (
                          <SelectItem key={bot.id} value={bot.id}>
                            {bot.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {chatbots.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        You don't have any AI Agents yet. <a href="/dashboard" className="underline">Create one</a> first.
                      </p>
                    )}
                  </div>
                  
                  <Button
                    onClick={handleSelectPage}
                    disabled={connecting || !selectedPage || !selectedChatbot}
                    className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700"
                  >
                    {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Connect Page
                  </Button>
                </div>
              </div>
            )}

            {/* Connect to Facebook (Initial State) */}
            {!status?.connected && step !== 'select-page' && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Facebook className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-3">
                    Connect Facebook Messenger
                  </h2>
                  
                  <p className="text-slate-600 dark:text-zinc-400 mb-8 max-w-md mx-auto">
                    Automate message replies on your Facebook Page with Nerava AI. 
                    Your customers will get instant responses 24/7.
                  </p>
                  
                  <Button
                    onClick={handleConnectFacebook}
                    size="lg"
                    className="gap-3 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg rounded-xl"
                  >
                    <Facebook className="w-6 h-6" />
                    Connect to Facebook
                  </Button>
                  
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-4">
                    You will be redirected to Facebook to log in
                  </p>
                </div>
                
                {/* Features */}
                <div className="mt-10 pt-8 border-t border-slate-200 dark:border-zinc-800">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 mb-4 text-center">
                    How it works
                  </h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="text-center p-4">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-lg">1</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-zinc-400">
                        Log in to your Facebook account
                      </p>
                    </div>
                    <div className="text-center p-4">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-lg">2</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-zinc-400">
                        Select the Page you want to connect
                      </p>
                    </div>
                    <div className="text-center p-4">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950/40 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-lg">3</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-zinc-400">
                        Done! AI starts replying to messages
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* No Chatbot Warning */}
            {chatbots.length === 0 && !status?.connected && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 mt-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                      Create an AI Agent First
                    </h3>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      You need to create an AI Agent before connecting Messenger.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/dashboard')}
                      className="mt-3"
                    >
                      Create AI Agent
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Back Button */}
            <div className="mt-8 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="gap-2 text-slate-600 dark:text-zinc-400"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default MessengerOnboarding;
